<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.sociograms', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <PageSectionCollapsible is-open>
            <template v-slot:title>
                {{ $t('messages.nexa_quick_guide') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <p class="mt-1 whitespace-pre-line col-span-8">
                    {{ $t('messages.nexa_quick_guide_sociogram_index_explanation') }}
                </p>
                <div class="col-span-4 hidden md:block">
                    <img src="/img/team_generator/nexa_guide_image_sociogram_index.webp" alt="" />
                </div>
            </div>
        </PageSectionCollapsible>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton element="InertiaLink" :href="meta.links.create" v-if="meta.can.create">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.group')" for="active" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            :null-option="true"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.quantity') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(sociogram, index) in sociograms.data"
                                :key="sociogram.id"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ sociogram.group_name }}
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-600"
                                >
                                    <div>
                                        {{ sociogram.opened_at }}
                                    </div>
                                    <div>
                                        {{ sociogram.closed_at }}
                                    </div>
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ sociogram.records_count }} /
                                    {{ sociogram.students_count }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton
                                        action="show"
                                        :href="sociogram.links.show"
                                        v-if="sociogram.can.show"
                                    />
                                    <InertiaSquareLinkButton
                                        action="edit"
                                        :href="sociogram.links.edit"
                                        v-if="sociogram.can.edit"
                                    />
                                    <InertiaSquareDeleteButton
                                        :href="sociogram.links.delete"
                                        v-if="sociogram.can.delete"
                                        :confirm-text="$t('messages.are_you_sure?')"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="sociograms" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import AppButton from '@/components/AppButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import PageSectionCollapsible from '../../../Shared/PageSectionCollapsible.vue';

export default {
    components: {
        PageSectionCollapsible,
        FormLabel,
        FormError,
        SubmitFormButton,
        FormSelect,
        AppButton,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        InertiaSquareDeleteButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'sociograms', 'options', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                group_id: this.filters.group_id,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
